/**
 * RUT chileno — validación y formato.
 *
 * El RUT (Rol Único Tributario) es el identificador fiscal chileno.
 * Formato canónico: NNNNNNNN-D donde D es un dígito verificador (0-9 o K)
 * calculado con el algoritmo módulo 11.
 *
 * Ejemplos válidos:
 *   "12.345.678-5"  (con puntos)
 *   "12345678-5"    (sin puntos)
 *   "12345678-K"    (dígito K)
 */

/** Elimina puntos, guiones y espacios; mayusculiza el dv. */
export function cleanRut(input: string): string {
  return input.replace(/[.\s-]/g, '').toUpperCase();
}

/** Calcula el dígito verificador según módulo 11. Devuelve '0'-'9' o 'K'. */
export function computeDv(body: string): string {
  if (!/^\d+$/.test(body)) {
    throw new Error('RUT body must contain only digits');
  }
  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += Number(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  const remainder = 11 - (sum % 11);
  if (remainder === 11) return '0';
  if (remainder === 10) return 'K';
  return String(remainder);
}

/**
 * Valida un RUT chileno.
 * Acepta formato con/sin puntos/guiones, case-insensitive.
 * Rechaza RUTs con body < 1M (personas naturales: >= 1000000) o > 99999999.
 */
export function isValidRut(input: string): boolean {
  if (typeof input !== 'string' || input.length === 0) return false;
  const cleaned = cleanRut(input);
  if (cleaned.length < 2) return false;
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  if (!/^\d+$/.test(body)) return false;
  if (!/^[0-9K]$/.test(dv)) return false;
  const num = Number(body);
  if (num < 1_000_000 || num > 99_999_999) return false;
  return computeDv(body) === dv;
}

/** Formato canónico: "12.345.678-5" (con puntos y guión). */
export function formatRut(input: string): string {
  const cleaned = cleanRut(input);
  if (cleaned.length < 2) throw new Error('Invalid RUT: too short');
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  const withDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withDots}-${dv}`;
}

/** Formato plano para persistencia: "123456785" (sin separadores). */
export function normalizeRut(input: string): string {
  return cleanRut(input);
}
